import React, { useEffect, useRef } from 'react';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import { useUnavailableDates } from '../../hooks/useUnavailableDates';
import { addOneDay } from '../../services/dateUtils';

const UserAvailabilityCalendar = ({ userId, bookingDates = [] }) => {
  const calendarRef = useRef(null);
  const calendarInstanceRef = useRef(null);
  const { unavailableDates, isLoading, error } = useUnavailableDates(userId);

  // Build/rebuild the calendar when unavailableDates loads
  useEffect(() => {
    if (!calendarRef.current || isLoading) return;

    if (calendarInstanceRef.current) {
      calendarInstanceRef.current.destroy();
      calendarInstanceRef.current = null;
    }

    const unavailableEvents = unavailableDates.map(ud => ({
      id: `unavailable-${ud.id}`,
      start: ud.start_date,
      end: addOneDay(ud.end_date),
      display: 'background',
      backgroundColor: '#ff000033',
      classNames: ['unavailable-date'],
    }));

    const bookingEvents = bookingDates.map((bd, i) => ({
      id: `booking-${i}`,
      start: bd.start_date,
      end: addOneDay(bd.end_date),
      display: 'background',
      backgroundColor: '#ff000033',
      classNames: ['unavailable-date', 'unavailable-date--booking'],
    }));

    const calendar = new Calendar(calendarRef.current, {
      plugins: [dayGridPlugin],
      initialView: 'dayGridMonth',
      events: [...unavailableEvents, ...bookingEvents],
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: '',
      },
      height: 500,
      editable: false,
      selectable: false,
      eventClick: function () {
        // read-only — no interaction
      },
    });

    calendar.render();
    calendarInstanceRef.current = calendar;

    return () => {
      if (calendarInstanceRef.current) {
        calendarInstanceRef.current.destroy();
        calendarInstanceRef.current = null;
      }
    };
  }, [unavailableDates, bookingDates, isLoading]);

  if (error) {
    return <p className="unavailability-calendar-error">{error}</p>;
  }

  return (
    <div className="user-availability-calendar">
      {isLoading && <p className="unavailability-calendar-loading">Loading availability…</p>}
      <div ref={calendarRef} />
      <p className="unavailability-calendar-legend">
        <span className="unavailability-calendar-legend__swatch" />
        Unavailable dates
      </p>
    </div>
  );
};

export default UserAvailabilityCalendar;
